import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { pickI18nString } from '@/lib/i18nUtils';
import { DEFAULT_LANG } from '@/i18n';
import type { ToolListItem } from '@/types/tool';

export type Tool = ToolListItem;

export const useToolsData = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery({
    queryKey: ['tools', lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select(`
          id,
          name,
          name_i18n,
          description,
          description_i18n,
          icon,
          slug,
          is_active,
          is_featured,
          is_coming_soon,
          order_index,
          tool_type_id,
          tool_type:tool_types(name),
          category:categories(name, name_i18n, description_i18n),
          tags:tool_tags(tags(name, name_i18n, color))
        `)
        .eq('is_active', true)
        .order('is_coming_soon', { ascending: true })
        .order('order_index', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;

      return (
        data?.map((tool: any) => {
          const localizedName = pickI18nString(tool.name_i18n, tool.name, lang, DEFAULT_LANG);
          const localizedDesc = pickI18nString(tool.description_i18n, tool.description, lang, DEFAULT_LANG);
          const localizedCategory = tool.category
            ? pickI18nString(tool.category.name_i18n, tool.category.name, lang, DEFAULT_LANG)
            : 'Other';
          const tags = (tool.tags || [])
            .map((tt: any) => (tt.tags ? tt.tags : tt))
            .filter(Boolean)
            .map((t: any) => ({
              name: pickI18nString(t.name_i18n, t.name, lang, DEFAULT_LANG),
              color: t.color,
            }));

          return {
            ...tool,
            name: localizedName,
            description: localizedDesc,
            category: localizedCategory,
            tool_type: tool.tool_type?.name || 'survey',
            tags,
          } as ToolListItem;
        }) || []
      );
    },
  });
};

export const useCategoriesData = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery({
    queryKey: ['categories', lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('name, name_i18n')
        .order('name');

      if (error) throw error;

      return ['All', ...(data?.map((cat: any) => pickI18nString(cat.name_i18n, cat.name, lang, DEFAULT_LANG)) || [])];
    },
  });
};